import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card, SectionTitle, Pill, Loader, EmptyState } from "@/components/common";
import { History, ArrowRight } from "lucide-react";

const ENTITY_TONE = { task: "info", project: "gold", settings: "warning", user: "success" };

function ago(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return d.toLocaleString();
  } catch { return iso; }
}

export default function Audit() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/audit");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader label="Loading activity" />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl tracking-tight" data-testid="page-title">Activity &amp; Audit</h1>
        <p className="text-muted-foreground text-sm mt-1">Every change across tasks, projects, settings and user accounts — who, when, and what changed.</p>
      </div>

      <section>
        <SectionTitle icon={History} hint={`${data.entries.length} recent changes`}>Change log</SectionTitle>
        {data.entries.length === 0 ? (
          <Card className="p-2"><EmptyState icon={History} title="No changes yet" subtitle="Edits made anywhere in the app will appear here." /></Card>
        ) : (
          <div className="space-y-2">
            {data.entries.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.015, 0.3) }}>
                <Card className="p-3.5" data-testid="audit-row">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Pill tone={ENTITY_TONE[e.entity_type] || "muted"}>{e.entity_type}</Pill>
                        <span className="font-medium text-sm">{e.entity_label}</span>
                        <span className="text-muted-foreground text-sm">· {e.field}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-sm">
                        <span className="text-muted-foreground line-through/60">{e.old_value}</span>
                        <ArrowRight size={13} className="text-gold shrink-0" />
                        <span className="text-foreground font-medium">{e.new_value}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-foreground/80">{e.actor_name}</div>
                      <div className="text-[11px] text-muted-foreground">{e.actor_role} · {ago(e.created_at)}</div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
